import React, { useState, useEffect, useCallback } from 'react'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Sheet,
  Stack,
  Typography,
  Input,
  Button,
  IconButton,
  Chip,
  Table,
  CircularProgress,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip,
} from '@mui/joy'
import {
  Search,
  Edit,
  DeleteOutline,
  Add,
  ArrowBack,
  ChevronLeft,
  ChevronRight,
  LocalOfferOutlined,
} from '@mui/icons-material'

import api from '../services/api'
// @ts-ignore - TagModal es JS/JSX sin tipos
import TagModal from '../components/TagModal'

const PAGE_LIMIT = 10

interface Tag {
  id: number
  name: string
  color: string
  ticketTags?: unknown[]
}

const toastError = (err: any) => {
  const errorMsg = err?.response?.data?.message || err?.message || 'Ocurrió un error'
  toast.error(errorMsg)
}

const TagsKanban: React.FC = () => {
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [count, setCount] = useState(0)
  const [tags, setTags] = useState<Tag[]>([])
  const [searchParam, setSearchParam] = useState('')

  const [selectedTag, setSelectedTag] = useState<Tag | null>(null)
  const [tagModalOpen, setTagModalOpen] = useState(false)
  const [deletingTag, setDeletingTag] = useState<Tag | null>(null)

  const totalPages = Math.max(1, Math.ceil(count / PAGE_LIMIT))

  const fetchTags = useCallback(async (page: number, search: string) => {
    setLoading(true)
    try {
      const { data } = await api.get('/tags/', {
        params: { searchParam: search, pageNumber: page, kanban: 1 },
      })
      setTags(data.tags || [])
      setCount(data.count || 0)
    } catch (err) {
      toastError(err)
      setTags([])
    } finally {
      setLoading(false)
    }
  }, [])

  // Búsqueda con debounce → reinicia a página 1
  useEffect(() => {
    const t = setTimeout(() => {
      setPageNumber(1)
      fetchTags(1, searchParam)
    }, 500)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam])

  // Cambio de página
  useEffect(() => {
    fetchTags(pageNumber, searchParam)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber])

  const reload = () => fetchTags(pageNumber, searchParam)

  const handleOpenNew = () => {
    setSelectedTag(null)
    setTagModalOpen(true)
  }

  const handleEdit = (tag: Tag) => {
    setSelectedTag(tag)
    setTagModalOpen(true)
  }

  const handleCloseModal = () => {
    setSelectedTag(null)
    setTagModalOpen(false)
  }

  const handleSaved = () => {
    handleCloseModal()
    reload()
  }

  const handleDelete = async (tagId: number) => {
    try {
      await api.delete(`/tags/${tagId}`)
      toast.success('Etiqueta eliminada con éxito')
      // Si era el último de la página, retrocede una página
      if (tags.length === 1 && pageNumber > 1) {
        setPageNumber(p => p - 1)
      } else {
        reload()
      }
    } catch (err) {
      toastError(err)
    } finally {
      setDeletingTag(null)
    }
  }

  const goPrev = () => setPageNumber(p => Math.max(1, p - 1))
  const goNext = () => setPageNumber(p => Math.min(totalPages, p + 1))

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.body' }}>
      {/* Cabecera */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2, flexWrap: 'wrap', gap: 1.5 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Sheet
            variant="soft"
            color="primary"
            sx={{ width: 40, height: 40, borderRadius: 'md', display: 'grid', placeItems: 'center' }}
          >
            <LocalOfferOutlined />
          </Sheet>
          <Box>
            <Typography level="h3" sx={{ lineHeight: 1.1 }}>
              Etiquetas del Funnel
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
              {count} etiqueta{count === 1 ? '' : 's'} en total
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Input
            size="sm"
            placeholder="Buscar etiquetas…"
            value={searchParam}
            onChange={e => setSearchParam(e.target.value.toLowerCase())}
            startDecorator={<Search fontSize="small" />}
            sx={{ minWidth: 240 }}
          />
          <Button size="sm" color="primary" startDecorator={<Add fontSize="small" />} onClick={handleOpenNew}>
            Añadir etiqueta
          </Button>
          <Button
            size="sm"
            variant="outlined"
            color="neutral"
            startDecorator={<ArrowBack fontSize="small" />}
            onClick={() => navigate('/funnel')}
          >
            Volver al Funnel
          </Button>
        </Stack>
      </Stack>

      {/* Tabla */}
      <Sheet
        variant="outlined"
        sx={{ borderRadius: 'md', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <Table
            stickyHeader
            hoverRow
            sx={{
              '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
              '--Table-headerUnderlineThickness': '1px',
              '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
              '& thead th:nth-of-type(1)': { width: '50%' },
              '& thead th': { fontWeight: 600 },
              '& td, & th': { textAlign: 'left', py: 1.25 },
              '& td:last-child, & th:last-child': { textAlign: 'right' },
              '& td:nth-of-type(2), & th:nth-of-type(2)': { textAlign: 'center' },
            }}
          >
            <thead>
              <tr>
                <th>Etiqueta</th>
                <th>Tickets</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {!loading && tags.length === 0 && (
                <tr>
                  <td colSpan={3}>
                    <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 6 }}>
                      <LocalOfferOutlined sx={{ fontSize: 36, color: 'text.tertiary' }} />
                      <Typography level="body-md" sx={{ color: 'text.tertiary' }}>
                        {searchParam ? 'Sin resultados para tu búsqueda' : 'Aún no hay etiquetas de funnel'}
                      </Typography>
                      {!searchParam && (
                        <Button size="sm" variant="soft" startDecorator={<Add fontSize="small" />} onClick={handleOpenNew}>
                          Crear la primera
                        </Button>
                      )}
                    </Stack>
                  </td>
                </tr>
              )}

              {tags.map(tag => (
                <tr key={tag.id}>
                  <td>
                    <Chip
                      size="md"
                      variant="soft"
                      startDecorator={
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: tag.color }} />
                      }
                      sx={{ bgcolor: `${tag.color}22`, color: tag.color, fontWeight: 600 }}
                    >
                      {tag.name}
                    </Chip>
                  </td>
                  <td>
                    <Chip size="sm" variant="soft" color="neutral">
                      {tag.ticketTags?.length ?? 0}
                    </Chip>
                  </td>
                  <td>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Editar">
                        <IconButton size="sm" variant="plain" color="neutral" onClick={() => handleEdit(tag)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="sm" variant="plain" color="danger" onClick={() => setDeletingTag(tag)}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </td>
                </tr>
              ))}

              {loading && (
                <tr>
                  <td colSpan={3}>
                    <Stack alignItems="center" sx={{ py: 4 }}>
                      <CircularProgress size="sm" />
                    </Stack>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Box>

        {/* Paginación */}
        <Divider />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1 }}
        >
          <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
            Página {pageNumber} de {totalPages}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              startDecorator={<ChevronLeft />}
              disabled={pageNumber <= 1 || loading}
              onClick={goPrev}
            >
              Anterior
            </Button>
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              endDecorator={<ChevronRight />}
              disabled={pageNumber >= totalPages || loading}
              onClick={goNext}
            >
              Siguiente
            </Button>
          </Stack>
        </Stack>
      </Sheet>

      {/* Modal crear/editar */}
      {tagModalOpen && (
        <TagModal
          open={tagModalOpen}
          onClose={handleCloseModal}
          onSaved={handleSaved}
          aria-labelledby="form-dialog-title"
          tagId={selectedTag?.id}
          kanban={1}
        />
      )}

      {/* Confirmación de borrado */}
      <Modal open={!!deletingTag} onClose={() => setDeletingTag(null)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>Eliminar etiqueta</DialogTitle>
          <Divider />
          <DialogContent>
            ¿Seguro que deseas eliminar la etiqueta <strong>{deletingTag?.name}</strong>? Los tickets dejarán de
            estar en esta etapa del funnel.
          </DialogContent>
          <DialogActions>
            <Button variant="solid" color="danger" onClick={() => deletingTag && handleDelete(deletingTag.id)}>
              Eliminar
            </Button>
            <Button variant="plain" color="neutral" onClick={() => setDeletingTag(null)}>
              Cancelar
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  )
}

export default TagsKanban
